import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import { useEmbedLy } from '@hivebrite/react-hooks';

import Embed from 'hb-react/shared/components/DesignSystem/Embed';

import WrapperContent from './WrapperContent';

const ExternalSourceThumbnail = ({ children, text, url, tag }) => {
  const { response, isLoading } = useEmbedLy(url, {
    apiUrl: `${window.location.origin}${Routes.embed_path()}`,
  });

  const embed = useMemo(() => response, [response]);

  if (!isEmpty(response) && embed) {
    return (
      <ExternalSourceVideoWrapper
        data-testid="external-embedly-wrapper"
        embed={embed}
      >
        <WrapperContent
          tag={tag}
          tabIndex={0}
          aria-label={s__(
            'Accessibility|Click to open the external link %{linkName} in a new tab',
          ).i__({
            linkName: text,
          })}
          css={css`
            & > div:first-child {
              height: 100%;
            }
            & > div:first-child > div {
              height: 100%;
            }
          `}
          preview={
            <Embed
              embed={embed}
              linkUrl={url}
              isLoading={isLoading}
              withDescription={false}
              renderEmptyPreview={(embed) => (
                <Box
                  height="100%"
                  width="100%"
                  alignItems="center"
                  justifyContent="center"
                  font="h2-regular"
                >
                  <Dotdotdot clamp={2}>{embed.provider_name}</Dotdotdot>
                </Box>
              )}
            />
          }
        >
          {children}
        </WrapperContent>
      </ExternalSourceVideoWrapper>
    );
  }

  return (
    <WrapperContent
      tag={tag}
      mx={{ _: 5, m: 0 }}
      tabIndex={0}
      data-testid="external-wrapper"
    >
      <Box
        width="100%"
        alignItems="center"
        justifyContent="center"
        font="h2-regular"
        textAlign="center"
        p={6}
      >
        <Dotdotdot clamp={2}>{children || text}</Dotdotdot>
      </Box>
    </WrapperContent>
  );
};

export default ExternalSourceThumbnail;

ExternalSourceThumbnail.propTypes = {
  children: PropTypes.node,
  text: PropTypes.node,
  url: PropTypes.string.isRequired,
};

export const ExternalSourceVideoWrapper = ({ children, embed }) => {
  const isEmbedVideoOnHtml = !!embed && embed.type === 'video' && !!embed.html;

  if (!isEmbedVideoOnHtml) {
    return children;
  }

  return (
    <Box
      display="block"
      aria-label={__('Start the video preview')}
      data-testid="embedly-wrapper"
    >
      {children}
    </Box>
  );
};

ExternalSourceVideoWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  embed: PropTypes.shape({
    html: PropTypes.any,
    type: PropTypes.string,
  }),
};
